import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { Skill } from '../types';
import { MOTION_GRAPHICS_SCENE_DESIGN_ID } from 'js/src/agent/tools/motion-graphics/motionGraphicsSceneDesign';
import { ASSET_TYPES } from 'js/src/agent/video-state/constants';

export const motionGraphicsScenesBasicsSkill: Skill = {
    name: SKILL_NAMES.motionGraphicsScenesBasics,
    description:
        "Bespoke animated graphics for content a stock scene design can't express — animated " +
        'numbers and data callouts, diagrams and step-by-step explainers, product-UI mockups, ' +
        'stylized typographic moments. They can stand alone as a scene or cover a span of ' +
        'narration. Load BEFORE adding or changing one.',
    kind: 'howto',
    surfaces: ['sketch'],
    get instructions(): string {
        return `# Motion Graphics Scenes

A motion graphics scene is a bespoke animated graphic — an HTML composition you write for this
video — rather than a stock scene design. **You author it yourself.**

\`${TOOL_NAMES.loadSkill}\` \`${SKILL_NAMES.htmlComposition}\` before you write any composition HTML. It
carries the document contract, the style rules a graphic is judged against, and the index of
animation techniques you can load — skip it and lint rejects a composition written to rules you
never read.

## When to reach for one
It is motion and bespoke layout that earn a motion graphics scene. A static stat on a
background is not the case for one — a stock design does that well.

A stock scene design suits footage or photography that should carry the scene, or a standard
text-and-media layout. Where footage, a talking head or a design should stay on screen, layer the
graphic over it instead — \`${SKILL_NAMES.layeredGraphicsBasics}\`.

## The build

**1. Add the scenes.** \`${TOOL_NAMES.addScene}\` with a \`scenes\` entry whose \`scene_design_id\` is
\`"${MOTION_GRAPHICS_SCENE_DESIGN_ID}"\` — the reserved id that adds a scene and clears it instead of
using one of the template's designs. Adding several? One call listing every scene in \`scenes\`, in
order. An added scene shows nothing until you design it, and the video state reports it as
\`status: empty (awaiting generation)\` until you do. Never leave one undesigned.

To change what a moment in the middle of a voiceover shows, use \`${TOOL_NAMES.addVoiceoverScene}\`
with the same reserved id, naming the transcript words the graphic should cover. The graphic
inherits the narration it covers, so you can build to what is being said. **The scene underneath
stays as the base — do not delete it.**

**2. Plan them all, in one call.** \`${TOOL_NAMES.writePlan}\` with a \`plans\` entry for every
scene, in play order. **You may not author a scene's HTML until its plan comes back approved.**
Planning them together is what lets their techniques be chosen as one video — varied where the
moments differ, alike where they repeat — and it costs one round trip instead of one per scene.

For each plan, write out the *exact words* of every text block, one entry per block, rather than
describing them — that list is the point of planning: to see all the copy in one spot. A graphic
that opens on an element carried in from the one before lists that element's words too: they are
on screen from its first frame.

Write \`layout\` in the video's design system, not in fresh numbers — "headline at hero size
top-left, the bar set in the plot box on the baseline" — so the graphic is placed against what
every other graphic was placed against.

A graphic that plots anything fills in \`data\` too: the composition is held to those figures,
so one that reaches the screen without passing through here is a number nobody agreed to.

If any plan is refused, none is approved: read why each refused one was, fix those, and send
every plan again in one call.

**3. Then build them one at a time**, in play order. **One scene per turn, and never batch these
steps across scenes**: each graphic is loaded, written and reviewed before the next is started.

1. \`${TOOL_NAMES.loadSkill}\` the techniques that scene's approved plan assigned you, in one turn.
   **\`techniques.required\` is an assignment, not a menu** — one or two skills chosen for the frame
   you described. Load every one \`techniques.instruction\` asks you to load
   that is not already in the conversation above — an earlier scene may have loaded it — and build
   every one. A technique loaded and not applied is the same as never loading it, and an entrance
   you wrote yourself is not a substitute for the technique the plan named.

   Build the frame around the techniques you were given rather than looking for a frame that suits
   them better — the assignment was made for the frame you described.
   Add the drawing skills on top **if the graphic depicts any object or symbol at all**.

   Do this before you write any HTML — the skills are working implementations, not descriptions, and
   one you have only read the name of produces a graphic that fades and slides instead of doing what
   the technique does. Loading nothing at all is the common failure, and it does not read as
   restraint: it reads as a graphic no one designed.
2. \`${TOOL_NAMES.writeHtmlAsset}\` with the \`scene_id\` and the full document. **The approved plan is
   binding**: any text on screen the plan does not contain is a blocking error, and the fix is to
   delete that text, not to add it to the plan afterwards.
3. Resolve any lint errors it reports.
4. \`${TOOL_NAMES.reviewSceneImages}\` with the \`scene_ids\` to confirm the motion actually renders —
   it scans the scene's whole timeline by default, which a single still cannot substitute for. The
   scan samples the scene evenly, so a phase that settles between two samples can go unchecked: for
   a graphic built in phases, follow up with one \`${TOOL_NAMES.reviewSceneImages}\` per phase, passing
   \`seconds\` for the moment that phase has landed.

   You wrote this graphic, so you are the last one who should be judging whether it worked — the
   tool hands the frames to a reviewer who has never seen it before. Treat what it reports as
   findings to act on, not as a confirmation to move past.
5. Act on what comes back. The tool releases a scene on its own after 3 fix rounds — a
   scene that keeps failing stops blocking automatically — so keep fixing with
   \`${TOOL_NAMES.editHtmlAsset}\` for as long as it keeps blocking, and don't stop early on
   your own judgment that a defect is unfixable. Match the fix to the finding: an object that
   reads as nothing wants simplifying or deleting, but a frame that only accumulates wants what
   has made its point to exit on the timeline once the narration is past it — deleting it from
   the composition instead costs you the thing you wrote. A misaligned element wants the two
   elements put in one coordinate frame — the same container, measured from the same origin —
   not a nudge of a few pixels.
   This review is internal: don't narrate it to the user.

You already have every composition you authored in this conversation. \`${TOOL_NAMES.readHtmlAsset}\`
is for when you don't: editing a graphic from an earlier session, or after an edit fails because your
copy has drifted.

## Changing an existing graphic
\`${TOOL_NAMES.editHtmlAsset}\` with the \`scene_id\` and the smallest set of find-and-replace edits
that does the job — prefer it over re-authoring. A revision that changes what the graphic says or how
long it runs needs a fresh approved plan first; a copy tweak, a colour or a timing value does not.

Refer to the parts listed for that scene in the video state so you know what you are changing ("the
38% headline", not "it").

## What the other tools cannot do to one
Its content is a single ${ASSET_TYPES.MOTION_GRAPHIC} composition. \`${TOOL_NAMES.setText}\`,
\`${TOOL_NAMES.setMedia}\` and the other asset tools do not apply to it — every change goes through
\`${TOOL_NAMES.editHtmlAsset}\` or \`${TOOL_NAMES.writeHtmlAsset}\`. This is the one exception to
"assets cannot be added or removed".

## Reference: the template's own designs
When a graphic needs more design direction than its neighbours give — it is the only scene, the
first, or the last, or the neighbour is full-bleed media — browse the template's scene designs:
\`${TOOL_NAMES.listSceneDesigns}\` to see how they look. Reference only; you still author your own
graphic.

---

Once the graphics are built, \`${SKILL_NAMES.reviewVideo}\` covers the review pass.`;
    },
};
